import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { IMAGES } from "@/constants/site";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay, testId, className = "" }) => (
  <span className="block overflow-hidden pb-2">
    <motion.span
      data-testid={testId}
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.2, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const go = (target) => (e) => {
    e.preventDefault();
    lenis?.scrollTo(target, { duration: 1.6 });
  };

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative h-[100svh] min-h-[620px] overflow-hidden">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Umi, oracle and rootworker, in a white headwrap and golden top"
          className="h-full w-full object-cover object-[70%_center]"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#131110] via-[#131110]/50 to-[#131110]/15" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#131110]/85 via-[#131110]/20 to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 flex h-full flex-col justify-end px-6 pb-28 md:px-12 lg:px-20">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
          className="mb-6 text-[11px] font-semibold uppercase tracking-[0.3em] text-[#D49A36] md:text-xs"
        >
          Umi Speaks — Oracle &amp; Rootworker — Mississippi
        </motion.p>

        <h1 className="font-display leading-[0.95] tracking-tight">
          <MaskedLine delay={0.4} testId="hero-greeting-1" className="text-5xl font-medium italic text-[#F2EFE9] sm:text-6xl md:text-7xl lg:text-8xl">
            Ahh, you made it.
          </MaskedLine>
          <MaskedLine delay={0.62} testId="hero-greeting-2" className="text-5xl font-medium italic text-[#D49A36] sm:text-6xl md:text-7xl lg:text-8xl">
            Welcome.
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-tagline"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.15 }}
          className="mt-8 text-xs font-bold uppercase tracking-[0.42em] text-[#DCD8D3] md:text-sm"
        >
          Culture <span className="mx-2 text-[#D49A36]">·</span> Candor <span className="mx-2 text-[#D49A36]">·</span> Community
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.35, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#about"
            data-testid="hero-cta-explore"
            onClick={go("#about")}
            className="border border-[#D49A36]/50 px-7 py-3 text-xs font-bold uppercase tracking-[0.22em] text-[#D49A36] transition-colors duration-300 hover:bg-[#D49A36] hover:text-[#131110]"
          >
            Step inside
          </a>
          <a
            href="#making-space"
            data-testid="hero-cta-join"
            onClick={go("#making-space")}
            className="bg-[#F2EFE9] px-7 py-3 text-xs font-bold uppercase tracking-[0.22em] text-[#131110] transition-colors duration-300 hover:bg-[#D49A36]"
          >
            Join the list
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        data-testid="hero-scroll-cue"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-10 right-8 z-10 hidden items-center gap-4 md:flex"
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.35em] text-[#DCD8D3]/70" style={{ writingMode: "vertical-rl" }}>
          Scroll
        </span>
        <span className="scroll-line relative block h-20 w-px overflow-hidden bg-[#F2EFE9]/15" />
      </motion.div>
    </section>
  );
}
